import { Box, Button, Link, Typography } from '@mui/material';
import { Helmet } from 'react-helmet';
import { StoryPageLayout } from '../layouts';

const PAGE_URL = 'https://www.rsmc.ai/updates/amazon-quick-phoenix-user-group';
const PAGE_TITLE = 'Amazon Quick Phoenix User Group';
const PAGE_DESCRIPTION =
  'RSMC joins the leadership team of the Amazon Quick Phoenix User Group. Join us at the next Phoenix meetup on October 22, 2026.';
const REGISTER_URL = 'https://luma.com/k1slcm9c';
const LINKEDIN_GROUP_URL = 'https://www.linkedin.com/groups/14419034/';

// Common paragraph styling
const paragraphSx = {
  fontSize: '1rem',
  lineHeight: 1.8,
  color: 'var(--color-text)',
  mb: 3,
};

// Section heading styling
const headingSx = {
  fontWeight: 700,
  mb: 3,
  mt: 6,
  color: 'var(--color-heading)',
  fontSize: { xs: '1.25rem', md: '1.5rem' },
};

// Bulleted list styling
const listSx = {
  ...paragraphSx,
  pl: 3,
  mt: 0,
  '& li': { mb: 1 },
  '& ul': { mt: 1, mb: 0 },
};

// Link styling
const linkSx = {
  color: 'var(--color-primary)',
  textDecoration: 'underline',
};

// Callout card styling, shared by the event details and the sidebar
const calloutSx = {
  border: '1px solid var(--color-border)',
  borderLeft: '4px solid var(--color-primary)',
  borderRadius: '8px',
  backgroundColor: 'var(--color-card)',
};

const registerButtonSx = {
  backgroundColor: 'primary.main',
  color: 'var(--color-primary-text)',
  fontWeight: 600,
  textTransform: 'none',
  '&:hover': { color: 'var(--color-primary-text)' },
};

const eventDetails = [
  'Amazon Quick User Group, Phoenix',
  'Thursday, October 22, 2026, 1:00 PM MT',
  'PHX12 Amazon Building, 100 S Mill Ave, Tempe, AZ 85281 (12th floor, rooms 12.405 to 12.406)',
];

const groupFounders = [
  'Gio Serrano',
  'Ashok Dasineni',
  'Venky Hosur',
  'Ismael Murillo',
  'Kristin Mandia',
];

/**
 * Link button to the Luma registration page for the October 22 meetup.
 *
 * @returns {import('react').ReactElement} Register button
 */
function RegisterButton() {
  return (
    <Button
      variant="contained"
      href={REGISTER_URL}
      target="_blank"
      rel="noopener noreferrer"
      aria-label="Register on Luma (opens in new window)"
      sx={registerButtonSx}
    >
      Register on Luma
    </Button>
  );
}

/**
 * Sidebar prompt above the subscribe form, pointing readers to the next
 * Phoenix meetup registration.
 *
 * @returns {import('react').ReactElement} Next meetup call to action
 */
function NextMeetup() {
  return (
    <Box sx={{ ...calloutSx, p: 3 }}>
      <Typography
        variant="h3"
        sx={{
          fontWeight: 700,
          fontSize: '1.125rem',
          color: 'var(--color-heading)',
          mb: 1,
        }}
      >
        Join us at the next Phoenix meetup
      </Typography>
      <Typography
        sx={{
          fontSize: '0.9375rem',
          lineHeight: 1.6,
          color: 'var(--color-text-muted)',
          mb: 2.5,
        }}
      >
        Thursday, October 22, 2026, 1:00 PM MT, in Tempe.
      </Typography>
      <RegisterButton />
    </Box>
  );
}

/**
 * Community news: RSMC joins the leadership team of the Amazon Quick Phoenix
 * User Group. Uses StoryPageLayout like the other articles, with the next
 * meetup and the subscribe form in the sidebar.
 *
 * @returns {import('react').ReactElement} Phoenix User Group update page
 */
function PhoenixUserGroupUpdate() {
  const structuredData = {
    '@context': 'https://schema.org',
    '@type': 'NewsArticle',
    headline: PAGE_TITLE,
    description: PAGE_DESCRIPTION,
    image:
      'https://www.rsmc.ai/images/amazon-quick-phoenix-user-group-meetup.jpg',
    author: {
      '@type': 'Organization',
      name: 'Ready Secure Managed Cloud',
    },
    publisher: {
      '@type': 'Organization',
      name: 'Ready Secure Managed Cloud',
      logo: {
        '@type': 'ImageObject',
        url: 'https://www.rsmc.ai/images/r-logo.png',
      },
    },
    datePublished: '2026-10-11',
    dateModified: '2026-10-11',
    mainEntityOfPage: {
      '@type': 'WebPage',
      '@id': PAGE_URL,
    },
  };

  return (
    <>
      <Helmet>
        <title>{PAGE_TITLE} | RSMC</title>
        <meta name="description" content={PAGE_DESCRIPTION} />
        <meta property="og:title" content={PAGE_TITLE} />
        <meta property="og:description" content={PAGE_DESCRIPTION} />
        <meta property="og:type" content="article" />
        <meta property="og:url" content={PAGE_URL} />
        <meta property="og:image" content="https://www.rsmc.ai/og-image.png" />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:image" content="https://www.rsmc.ai/og-image.png" />
        <meta name="twitter:title" content={PAGE_TITLE} />
        <meta name="twitter:description" content={PAGE_DESCRIPTION} />
        <meta
          name="keywords"
          content="Amazon Quick, Amazon Quick User Group, Phoenix, Tempe, agentic AI, Rochelle Manns, RSMC, community"
        />
        <link rel="canonical" href={PAGE_URL} />
        <script type="application/ld+json">
          {JSON.stringify(structuredData)}
        </script>
      </Helmet>

      <StoryPageLayout
        title="Community News"
        subtitle="RSMC Joins the Phoenix User Group Leadership Team"
        formHeading="Get RSMC Updates"
        formFilePath="/forms/subscribe-form.txt"
        sidebarContent={<NextMeetup />}
        errorMessage="Unable to load subscribe form"
        loadingMessage="Loading subscribe form..."
      >
        <Box component="article">
          {/* Article Title */}
          <Typography
            variant="h1"
            sx={{
              fontWeight: 700,
              fontSize: { xs: '1.5rem', md: '1.75rem' },
              color: 'var(--color-heading)',
              mb: 3,
            }}
          >
            {PAGE_TITLE}
          </Typography>

          {/* Editorial meta row */}
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              flexWrap: 'wrap',
              gap: 1.5,
              mb: 4,
            }}
          >
            {['Ready Secure Managed Cloud', 'October 2026', '3 min read'].map(
              (item, index) => (
                <Box
                  key={item}
                  sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}
                >
                  {index > 0 && (
                    <Box
                      component="span"
                      aria-hidden="true"
                      sx={{
                        width: 4,
                        height: 4,
                        borderRadius: '50%',
                        backgroundColor: 'var(--color-primary)',
                      }}
                    />
                  )}
                  <Typography
                    component="span"
                    sx={{
                      fontSize: '0.8125rem',
                      fontWeight: 600,
                      letterSpacing: '0.5px',
                      color: 'var(--color-text-muted)',
                    }}
                  >
                    {item}
                  </Typography>
                </Box>
              )
            )}
          </Box>

          <Typography sx={paragraphSx}>
            RSMC is proud to share that we have joined the leadership team of
            the Amazon Quick Phoenix User Group. Our founder and CIO, Rochelle
            Manns, now helps guide a growing community of Amazon Quick users
            across the Valley, and we are excited to invest in the people
            building the future of agentic AI right here in Phoenix.
          </Typography>

          {/* Meetup photo. The original is portrait, so it is cropped to 4:3
              around the table and the agenda screen. */}
          <Box
            component="img"
            src="/images/amazon-quick-phoenix-user-group-meetup.jpg"
            alt="Members of the Amazon Quick Phoenix User Group at a recent meetup"
            sx={{
              display: 'block',
              width: '100%',
              aspectRatio: '4 / 3',
              objectFit: 'cover',
              objectPosition: 'center 45%',
              borderRadius: '8px',
              mb: 3,
            }}
          />

          <Typography variant="h2" sx={headingSx}>
            What the Amazon Quick User Groups are all about
          </Typography>

          <Typography sx={paragraphSx}>
            Amazon Quick User Groups are customer-driven communities. They bring
            local professionals together to share knowledge, best practices, and
            real experiences with the platform. These groups are led by users,
            for users, and that is what makes them so valuable.
          </Typography>

          <Typography sx={paragraphSx}>Members come together to:</Typography>

          <Box component="ul" sx={listSx}>
            <li>
              Exchange ideas and learnings about Amazon Quick, including how to
              build custom agents and use unified AI agents for research,
              business insights, and automation.
            </li>
            <li>
              Meet other professionals in the region who are applying Amazon
              Quick to enterprise AI, data analytics, and workplace
              productivity.
            </li>
            <li>
              Explore new use cases, trade solutions, and work through
              real-world challenges together.
            </li>
            <li>
              Learn about the latest features, updates, and best practices
              directly from peers and experts.
            </li>
            <li>
              Build a local support network that strengthens the broader Amazon
              Quick community.
            </li>
          </Box>

          <Typography sx={paragraphSx}>
            There are now 15 community-led Amazon Quick User Groups across the
            Americas and the EMEA region, with in-person meetups in cities
            including Phoenix, Austin, Boston, Los Angeles, Chicago, New York,
            Houston, and London. In May alone, the Austin group welcomed more
            than 100 attendees and Boston drew more than 90. Phoenix is part of
            this fast-growing network, and the energy keeps building with every
            session.
          </Typography>

          <Typography variant="h2" sx={headingSx}>
            Join us at the next Phoenix meetup
          </Typography>

          <Typography sx={paragraphSx}>
            Here are the details for our next gathering. Bring your laptop,
            because this is a hands-on session built around the GA experience.
          </Typography>

          {/* Event details callout */}
          <Box sx={{ ...calloutSx, p: { xs: 2.5, md: 3 }, mb: 3 }}>
            {eventDetails.map((line) => (
              <Typography
                key={line}
                sx={{
                  fontWeight: 700,
                  lineHeight: 1.6,
                  color: 'var(--color-heading)',
                  mb: 0.5,
                }}
              >
                {line}
              </Typography>
            ))}
          </Box>

          <Typography sx={paragraphSx}>What is on the agenda:</Typography>

          <Box component="ul" sx={listSx}>
            <li>
              Welcome, around-the-room introductions, and community updates with
              Rochelle Manns, CIO of RSMC.
            </li>
            <li>
              Web, Mobile, and Desktop: understanding Quick&rsquo;s cloud
              runtime unification, with Andy Son, Solutions Architect at AWS.
            </li>
            <li>
              Hands-on exploration of the GA experience, including:
              <ul>
                <li>
                  Amazon Quick Desktop: one companion across every surface
                </li>
                <li>Local file intelligence and on-device execution</li>
                <li>Agents and Schedules: putting work on automation</li>
                <li>Spaces, Topics, and conversational analytics</li>
                <li>Flows, Automate, and Research: the unified workflow</li>
              </ul>
            </li>
          </Box>

          <Typography sx={paragraphSx}>What to bring:</Typography>

          <Box component="ul" sx={listSx}>
            <li>A laptop</li>
            <li>A government-issued ID (required for building check-in)</li>
          </Box>

          <Typography sx={paragraphSx}>
            Parking is validated. Park in the garage on Second Street at the
            PHX12 Amazon Building, take the garage elevator to the first floor,
            cross the lobby to the second set of elevators, and go up to the
            12th floor. Follow the signs to Amazon Reception for your visitor
            badge and parking validation. Please plan to arrive about 15 minutes
            early.
          </Typography>

          <Box sx={{ textAlign: 'center', my: 4 }}>
            <RegisterButton />
          </Box>

          <Typography variant="h2" sx={headingSx}>
            We Build with Quick, We Use Quick. It just made sense to join the
            leadership team.
          </Typography>

          <Typography sx={paragraphSx}>
            At RSMC, our mission is to help mission-driven organizations adopt
            cloud and AI with confidence. Joining the Phoenix leadership team is
            a natural extension of that work. We get to roll up our sleeves
            alongside fellow practitioners, share what we are learning, and help
            more people get hands-on value from Amazon Quick.
          </Typography>

          <Typography sx={paragraphSx}>
            The Phoenix User Group exists thanks to the Amazonians who founded
            it and continue to lead it.
          </Typography>

          <Box component="ul" sx={listSx}>
            {groupFounders.map((name) => (
              <li key={name}>{name}</li>
            ))}
          </Box>

          <Typography variant="h2" sx={headingSx}>
            Get involved
          </Typography>

          <Typography sx={paragraphSx}>
            The best way to stay current is to join the community. Connect with
            us in the{' '}
            <Link
              href={LINKEDIN_GROUP_URL}
              target="_blank"
              rel="noopener noreferrer"
              sx={linkSx}
            >
              Amazon Quick Phoenix User Group on LinkedIn
            </Link>{' '}
            for meetup announcements and updates, and register for the October
            22 session above. Whether you are brand new to agentic AI or already
            building with Amazon Quick, you will find a welcoming group of peers
            ready to learn alongside you.
          </Typography>

          <Typography sx={paragraphSx}>
            We look forward to seeing you in Phoenix.
          </Typography>

          {/* Visual separator - R logo */}
          <Box sx={{ textAlign: 'center', mt: 6, mb: 2 }}>
            <Box
              component="img"
              src="/images/r-logo.png"
              alt="Ready Secure Managed Cloud"
              sx={{ width: '50px', height: 'auto', opacity: 0.7 }}
            />
          </Box>
        </Box>
      </StoryPageLayout>
    </>
  );
}

export default PhoenixUserGroupUpdate;
